import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ModernNavigation } from '../components/modern/ModernNavigation';
import { ModernFooter } from '../components/modern/ModernFooter';
import { SharedAssessment } from '../types/sharedAssessment';
import {
  formatCurrency,
  formatPercentage,
  formatDate,
  getRecommendedOption,
  calculateFirstYearImpact
} from '../utils/assessmentHelpers';
import '../css/modern-design-system.css';
import '../css/modern-shared-assessment.css';

export function SharedAssessmentPage() {
  const { token } = useParams<{ token: string }>();
  const [assessment, setAssessment] = useState<SharedAssessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Refinance Assessment Results - EquityWorkx';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'View detailed refinance assessment analysis');
    }
    
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex';
      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    async function loadAssessment() {
      if (!token) {
        setError('No assessment token provided.');
        setLoading(false);
        return;
      }

      try {
        const functions = getFunctions();
        const getShared = httpsCallable(functions, 'getSharedRefiAssessment');
        const result = await getShared({ shareToken: token });
        setAssessment(result.data as SharedAssessment);
      } catch (err: any) {
        console.error('Error loading assessment:', err);
        
        if (err.code === 'functions/not-found') {
          setError('This shared assessment could not be found.');
        } else if (err.code === 'functions/failed-precondition') {
          setError('This shared assessment link has expired.');
        } else {
          setError('An error occurred loading this assessment.');
        }
      } finally {
        setLoading(false);
      }
    }

    loadAssessment();
  }, [token]);

  // Loading State
  if (loading) {
    return (
      <div className="shared-assessment-page">
        <ModernNavigation scrolled={scrolled} />
        <div className="assessment-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !assessment) {
    return (
      <div className="shared-assessment-page">
        <ModernNavigation scrolled={scrolled} />
        <div className="assessment-error">
          <div className="error-icon">❌</div>
          <h1 className="error-message">{error || 'Assessment Not Found'}</h1>
          <p className="error-description">
            Please check the link and try again, or contact the person who shared this assessment with you.
          </p>
        </div>
        <ModernFooter />
      </div>
    );
  }

  const { analysisData, aiAssessment, expiresAt, viewCount } = assessment;
  const { comparison, noFeeSavingsOverTime, fullFeeSavingsOverTime, equityAppreciation } = analysisData;

  // The calculator returns 'none' when neither option beats the loan the
  // homeowner already has. This used to fall through to the full-fee branch,
  // so an assessment that concluded "do not refinance" was presented here as
  // "Recommended: Full-Fee Refinance" - the opposite of its own finding, and
  // the opposite of what the same share link shows in the mobile app.
  const noBeneficialOption = comparison.recommendation === 'none';
  const recommendedOption = comparison.recommendation === 'noFee'
    ? comparison.noFeeOption
    : comparison.fullFeeOption;

  // Prepare chart data
  const chartData = [5, 10, 15, 20, 25, 30].map(year => ({
    year,
    noFee: noFeeSavingsOverTime[year]?.totalSavings || 0,
    fullFee: fullFeeSavingsOverTime[year]?.totalSavings || 0,
  }));

  return (
    <div className="shared-assessment-page">
      <ModernNavigation scrolled={scrolled} />

      <div className="assessment-content">
        {/* Hero Section */}
        <section className="assessment-hero">
          <div className={`recommendation-badge ${noBeneficialOption ? 'recommendation-badge-neutral' : ''}`}>
            {!noBeneficialOption && <span className="recommendation-badge-icon">⭐</span>}
            <span>
              {noBeneficialOption
                ? getRecommendedOption(comparison.recommendation)
                : `Recommended: ${getRecommendedOption(comparison.recommendation)}`}
            </span>
          </div>

          <h1 className="hero-title">Your Refinance Assessment Results</h1>
          <p className="hero-subtitle">
            {noBeneficialOption
              ? "Based on current market conditions and your loan details, neither refinance option would lower your monthly payment. Holding your current loan is the stronger position for now."
              : "Based on current market conditions and your loan details, here's your personalized refinance analysis."}
          </p>

          {!noBeneficialOption && (
            <div className="hero-metrics">
              <div className="hero-metric">
                <div className="hero-metric-label">Monthly Savings</div>
                <div className="hero-metric-value">{formatCurrency(recommendedOption.monthlySavings)}</div>
              </div>
              <div className="hero-metric">
                <div className="hero-metric-label">Total Net Savings</div>
                <div className="hero-metric-value">{formatCurrency(recommendedOption.netSavings)}</div>
              </div>
            </div>
          )}
        </section>

        {/* Key Metrics Cards - only meaningful when an option was actually recommended */}
        {!noBeneficialOption && (
          <section className="key-metrics">
            <div className="metric-card">
              <div className="metric-icon">💰</div>
              <div className="metric-label">New Monthly Payment</div>
              <div className="metric-value">{formatCurrency(recommendedOption.monthlyPayment)}</div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">📊</div>
              <div className="metric-label">Interest Rate</div>
              <div className="metric-value">{formatPercentage(recommendedOption.rate, 3)}</div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">⏱️</div>
              <div className="metric-label">Breakeven Point</div>
              <div className="metric-value">{Math.round(recommendedOption.breakeven)} months</div>
            </div>
          </section>
        )}

        {/* ROI Highlight */}
        {!noBeneficialOption && (
          <section className="roi-highlight">
            <div className="roi-label">First Year Impact</div>
            <div className="roi-value">{formatCurrency(calculateFirstYearImpact(recommendedOption.monthlySavings))}</div>
            <div className="roi-description">Total savings in your first year</div>
          </section>
        )}

        {/* Detailed Comparison */}
        <section className="comparison-section">
          <h2 className="section-title">Detailed Comparison</h2>
          
          <div className="comparison-grid">
            {/* No-Fee Option */}
            <div className={`comparison-card ${comparison.recommendation === 'noFee' ? 'recommended' : ''}`}>
              {comparison.recommendation === 'noFee' && (
                <div className="recommended-badge">✓ Recommended</div>
              )}
              
              <div className="comparison-header">
                <div className="comparison-title">No-Fee Refinance</div>
                <div className="comparison-rate">{formatPercentage(comparison.noFeeOption.rate, 3)}</div>
              </div>
              
              <div className="comparison-details">
                <div className="detail-row">
                  <span className="detail-label">Monthly Payment</span>
                  <span className="detail-value">{formatCurrency(comparison.noFeeOption.monthlyPayment)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Monthly Savings</span>
                  <span className="detail-value positive">{formatCurrency(comparison.noFeeOption.monthlySavings)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Closing Costs</span>
                  <span className="detail-value">{formatCurrency(comparison.noFeeOption.closingCosts)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Breakeven</span>
                  <span className="detail-value">{Math.round(comparison.noFeeOption.breakeven)} months</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Net Savings</span>
                  <span className="detail-value positive">{formatCurrency(comparison.noFeeOption.netSavings)}</span>
                </div>
              </div>
            </div>

            {/* Full-Fee Option */}
            <div className={`comparison-card ${comparison.recommendation === 'fullFee' ? 'recommended' : ''}`}>
              {comparison.recommendation === 'fullFee' && (
                <div className="recommended-badge">✓ Recommended</div>
              )}
              
              <div className="comparison-header">
                <div className="comparison-title">Full-Fee Refinance</div>
                <div className="comparison-rate">{formatPercentage(comparison.fullFeeOption.rate, 3)}</div>
              </div>
              
              <div className="comparison-details">
                <div className="detail-row">
                  <span className="detail-label">Monthly Payment</span>
                  <span className="detail-value">{formatCurrency(comparison.fullFeeOption.monthlyPayment)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Monthly Savings</span>
                  <span className="detail-value positive">{formatCurrency(comparison.fullFeeOption.monthlySavings)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Closing Costs</span>
                  <span className="detail-value">{formatCurrency(comparison.fullFeeOption.closingCosts)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Breakeven</span>
                  <span className="detail-value">{Math.round(comparison.fullFeeOption.breakeven)} months</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Net Savings</span>
                  <span className="detail-value positive">{formatCurrency(comparison.fullFeeOption.netSavings)}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Savings Over Time Chart */}
        <section className="chart-section">
          <h2 className="chart-title">Savings Comparison Over Time</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="year" 
                  label={{ value: 'Years', position: 'insideBottom', offset: -5 }}
                  stroke="#6b7280"
                />
                <YAxis 
                  label={{ value: 'Total Savings ($)', angle: -90, position: 'insideLeft' }}
                  stroke="#6b7280"
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value)}
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="noFee" 
                  stroke="#2dbce8" 
                  strokeWidth={3}
                  name="No-Fee Option"
                  dot={{ fill: '#2dbce8', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="fullFee" 
                  stroke="#9DBF72" 
                  strokeWidth={3}
                  name="Full-Fee Option"
                  dot={{ fill: '#9DBF72', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Long-Term Analysis Tables */}
        <section className="tables-section">
          {/* Interest Savings Table */}
          <div className="table-card">
            <h3 className="table-title">Interest Savings Over Time</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Years</th>
                  <th>No-Fee Option</th>
                  <th>Full-Fee Option</th>
                </tr>
              </thead>
              <tbody>
                {[5, 10, 15, 20, 25, 30].map(year => (
                  <tr key={year}>
                    <td>{year} years</td>
                    <td>{formatCurrency(noFeeSavingsOverTime[year]?.interestSavings || 0)}</td>
                    <td>{formatCurrency(fullFeeSavingsOverTime[year]?.interestSavings || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Payment Savings Table */}
          <div className="table-card">
            <h3 className="table-title">Payment Savings Over Time</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Years</th>
                  <th>No-Fee Option</th>
                  <th>Full-Fee Option</th>
                </tr>
              </thead>
              <tbody>
                {[5, 10, 15, 20, 25, 30].map(year => (
                  <tr key={year}>
                    <td>{year} years</td>
                    <td>{formatCurrency(noFeeSavingsOverTime[year]?.paymentSavings || 0)}</td>
                    <td>{formatCurrency(fullFeeSavingsOverTime[year]?.paymentSavings || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Equity Appreciation Table */}
          <div className="table-card">
            <h3 className="table-title">Projected Equity Appreciation</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Years</th>
                  <th>Future Value</th>
                  <th>Appreciation</th>
                  <th>Total % Gain</th>
                </tr>
              </thead>
              <tbody>
                {[5, 10, 15, 20, 25, 30].map(year => {
                  const data = equityAppreciation[year];
                  return data ? (
                    <tr key={year}>
                      <td>{year} years</td>
                      <td>{formatCurrency(data.futureValue)}</td>
                      <td>{formatCurrency(data.appreciation)}</td>
                      <td>{formatPercentage(data.totalPercentGain, 2)}</td>
                    </tr>
                  ) : null;
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* AI Expert Analysis */}
        <section className="ai-assessment">
          <div className="ai-header">
            <h2 className="ai-title">
              <span className="ai-icon">🤖</span>
              AI Expert Analysis
            </h2>
            <div className="market-rate-badge">
              Current Market Rate: {formatPercentage(aiAssessment.marketRate, 3)}
            </div>
          </div>
          
          <div className="ai-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiAssessment.recommendation}</ReactMarkdown>
          </div>
        </section>

        {/* Call-to-Action Section */}
        <section className="cta-section">
          <h2 className="cta-title">Get Your Own Assessment</h2>
          <p className="cta-subtitle">
            Join thousands of homeowners who are making smarter refinance decisions with EquityWorkx.
            Get your personalized assessment today.
          </p>
          <a href="/download" className="cta-button">
            Download EquityWorkx
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7 10H13M13 10L10 7M13 10L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </section>

        {/* Footer Information */}
        <section className="assessment-footer">
          <div className="footer-info">
            <div className="footer-item">
              <strong>Assessment expires on:</strong> {formatDate(expiresAt)}
            </div>
            <div className="footer-item">
              <strong>Viewed:</strong> {viewCount} {viewCount === 1 ? 'time' : 'times'}
            </div>
          </div>
          
          <div className="footer-branding">
            <img src="/equityworkx_logo.png" alt="EquityWorkx" className="footer-logo" />
          </div>

          <div className="footer-disclaimer">
            <strong>IMPORTANT DISCLAIMER:</strong> This report is for informational purposes only and does not constitute financial, legal, or licensed mortgage advice.
            All estimates are based on assumptions and actual rates, terms, and costs may vary. This analysis is not a commitment to lend. Consult with a
            licensed loan officer before making any refinancing decisions. EquityWorkx is not a licensed mortgage lender or broker.
          </div>
        </section>
      </div>

      <ModernFooter />
    </div>
  );
}

